import { reactive, ref } from 'vue'
import { apiFetch } from './api'

export interface ConversationPreview {
  id: string
  otherUsername: string
  otherName: string
  otherAvatar: string
  lastMessageAt: string
  lastMessageText: string
  lastMessageBy: string
  unreadCount: number
}

export interface Message {
  id: string
  systemId: string
  conversationId: string
  senderUsername: string
  text: string
  readAt: string
  createdAt: string
}

const state = reactive({
  conversations: [] as ConversationPreview[],
  unreadTotal: 0,
  loading: false
})

const activeConversationId = ref<string | null>(null)
const activeMessages = ref<Message[]>([])
const messagesLoading = ref(false)

export const messengerStore = {
  state,
  activeConversationId,
  activeMessages,
  messagesLoading,

  async loadConversations() {
    state.loading = true
    try {
      state.conversations = await apiFetch<ConversationPreview[]>('/conversations')
      state.unreadTotal = state.conversations.reduce((sum, c) => sum + c.unreadCount, 0)
    } catch {
      // ignore
    } finally {
      state.loading = false
    }
  },

  async openConversationWith(withUsername: string): Promise<string> {
    const resp = await apiFetch<{ id: string }>('/conversations/open', {
      method: 'POST',
      body: JSON.stringify({ withUsername })
    })
    return resp.id
  },

  async loadMessages(conversationId: string) {
    messagesLoading.value = true
    activeConversationId.value = conversationId
    try {
      activeMessages.value = await apiFetch<Message[]>(`/conversations/${conversationId}/messages`)
      // Update unread in local state
      const conv = state.conversations.find(c => c.id === conversationId)
      if (conv) {
        state.unreadTotal = Math.max(0, state.unreadTotal - conv.unreadCount)
        conv.unreadCount = 0
      }
    } catch {
      // ignore
    } finally {
      messagesLoading.value = false
    }
  },

  async sendMessage(conversationId: string, text: string, senderUsername: string) {
    const msg = await apiFetch<{ id: string; text: string; createdAt: string }>(
      `/conversations/${conversationId}/messages`,
      { method: 'POST', body: JSON.stringify({ text }) }
    )
    const newMsg: Message = {
      id: msg.id,
      systemId: '',
      conversationId,
      senderUsername,
      text: msg.text,
      readAt: '',
      createdAt: msg.createdAt
    }
    activeMessages.value = [...activeMessages.value, newMsg]
    // Update conversation preview
    const conv = state.conversations.find(c => c.id === conversationId)
    if (conv) {
      conv.lastMessageText = text.slice(0, 80)
      conv.lastMessageAt = msg.createdAt
      conv.lastMessageBy = senderUsername
    }
    return newMsg
  },

  async pollMessages(conversationId: string) {
    try {
      const msgs = await apiFetch<Message[]>(`/conversations/${conversationId}/messages`)
      activeMessages.value = msgs
    } catch {
      // ignore
    }
  },

  async refreshUnread() {
    try {
      const resp = await apiFetch<{ unread: number }>('/conversations/unread-count')
      state.unreadTotal = resp.unread
    } catch {
      // ignore
    }
  }
}
